import { Component, Injector, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { IIKOTableGridModule } from '../lib/iiko-table-grid.module';
import { CellComponent, ColumnDefinition, TabulatorFull } from 'tabulator-tables';
import { CellClickedEventDataInterface } from '../lib';
import { DemoService } from '../services/demo.service';
import moment from 'moment';
import { createCustomElement, NgElement, NgElementConstructor, WithProperties } from '@angular/elements';
import { CustomButtonComponent } from '../custom-button/custom-button.component';
import {
  ProcessRequestDemoPayload,
  RequestDemoApiResponse,
  RequestDemoInterface,
  RequestDemoStatus
} from '../interfaces/request-demo.interface';
import {
  RequestDemoDetailDialogComponent,
  RequestDemoDetailDialogData
} from './request-demo-detail-dialog.component';

@Component({
  selector: 'app-request-demo',
  standalone: true,
  imports: [
    IIKOTableGridModule
  ],
  templateUrl: './request-demo.component.html',
  styleUrl: './request-demo.component.scss'
})
export class RequestDemoComponent implements OnInit {
  table: TabulatorFull | any;

  readonly processButtonLabel = $localize`:@@requestDemoProcessButton|Кнопка обработать:Process`;
  readonly doneButtonLabel = $localize`:@@requestDemoDoneButton|Кнопка завершить:Done`;
  readonly viewDetailsLabel = $localize`:@@requestDemoViewDetailsButton|Открыть детали:View details`;
  readonly viewColumnTitle = $localize`:@@requestDemoColumnView|Просмотр заявки:View`;

  readonly statusLabels: Record<RequestDemoStatus, string> = {
    resolved: $localize`:@@requestDemoStatusResolved|Статус обработано:Resolved`,
    done: $localize`:@@requestDemoStatusDone|Статус выполнено:Done`,
    in_progress: $localize`:@@requestDemoStatusInProgress|Статус в обработке:In progress`,
    new: $localize`:@@requestDemoStatusNew|Статус новая:New`
  };

  columnDefs: ColumnDefinition[] = [
    {
      title: $localize`:@@requestDemoColumnId|Идентификатор заявки:ID`,
      field: 'id',
      width: 80,
      headerSort: true,
    },
    {
      title: this.viewColumnTitle,
      field: 'view',
      width: 80,
      hozAlign: 'center',
      headerHozAlign: 'center',
      headerSort: false,
      formatter: (cell) => this.viewFormatter(cell)
    },
    {
      title: $localize`:@@requestDemoColumnFio|ФИО клиента:Full name`,
      field: 'fio',
      width: 250,
      headerSort: true,
    },
    {
      title: $localize`:@@requestDemoColumnStatus|Статус заявки:Status`,
      field: 'status',
      width: 150,
      headerSort: true,
      formatter: (cell) => this.statusFormatter(cell)
    },
    {
      title: $localize`:@@requestDemoColumnAction|Действие обработать:Action`,
      field: 'action',
      width: 160,
      headerSort: false,
      formatter: (cell) => this.customFormatter(cell)
    },
    {
      title: $localize`:@@requestDemoColumnEmail|Электронная почта:Email`,
      field: 'email',
      width: 200,
      headerSort: true,
    },
    {
      title: $localize`:@@requestDemoColumnPhone|Телефон:Phone`,
      field: 'phone',
      width: 180,
      headerSort: true,
    },
    {
      title: $localize`:@@requestDemoColumnDescription|Описание запроса:Description`,
      field: 'description',
      width: 320,
      headerSort: true,
    },
    {
      title: $localize`:@@requestDemoColumnCreatedAt|Дата создания:Created`,
      field: 'createdAt',
      width: 200,
      headerSort: true,
      formatter: (cell) => {
        const value = cell.getValue();
        return value ? moment(value).format('DD.MM.YYYY HH:mm') : '—';
      }
    },
    {
      title: $localize`:@@requestDemoColumnUpdatedAt|Дата обновления:Updated`,
      field: 'updatedAt',
      width: 200,
      headerSort: true,
      formatter: (cell) => {
        const value = cell.getValue();
        return value ? moment(value).format('DD.MM.YYYY HH:mm') : '—';
      }
    },


  ];

  tableData: RequestDemoInterface[] = [];

  constructor(
    public injector: Injector,
    private demoService: DemoService,
    private dialog: MatDialog) {
  }

  ngOnInit() {
    this.defineCustomButtons();
    this.loadRequestDemoList();
  }

  defineCustomButtons() {
    if (!customElements.get('app-custom-button')) {
      const myElement = createCustomElement(CustomButtonComponent, {
        injector: this.injector,
      }) as NgElementConstructor<unknown>;
      customElements.define('app-custom-button', myElement);
    }
  }


  onCellClicked($event: CellClickedEventDataInterface) {
    console.log('cell clicked', $event);
  }

  tableInstanceChange($event: any) {
    this.table = $event;
  }

  private customFormatter(cell: CellComponent) {
    cell.getElement().classList.add('py6');
    const data = cell.getData() as RequestDemoInterface;
    const action = this.resolveAction(data.status);

    if (!action) {
      return document.createElement('span');
    }

    const myElement =
      document.createElement('app-custom-button') as NgElement &
        WithProperties<CustomButtonComponent>;
    myElement.title = action.label;
    myElement.addEventListener('clickMyButton', () => {
      this.processRequest(data, action.nextStatus);
    });

    return myElement;
  }

  private viewFormatter(cell: CellComponent) {
    const container = document.createElement('button');
    container.type = 'button';
    container.classList.add('view-button');
    container.setAttribute('aria-label', this.viewDetailsLabel);
    container.title = this.viewDetailsLabel;
    container.innerHTML = '<span class="material-icons">visibility</span>';
    container.addEventListener('click', () => {
      const data = cell.getData() as RequestDemoInterface;
      this.openDetailsDialog(data);
    });
    return container;
  }

  private statusFormatter(cell: CellComponent) {
    const status = cell.getValue() as RequestDemoStatus;
    const badge = document.createElement('span');
    badge.classList.add('status-badge', `status-${status}`);
    badge.textContent = this.statusLabels[status] ?? status;
    return badge;
  }

  private mapToRequest(item: RequestDemoApiResponse): RequestDemoInterface {
    const status = this.resolveStatus(item);
    return {
      id: item.id,
      fio: item.fio,
      phone: item.phone,
      description: item.description,
      email: item.email,
      status,
      createdAt: item.createdAt ?? item.date_create ?? '',
      updatedAt: item.updatedAt ?? item.date_update ?? '',
    };
  }

  private resolveStatus(item: RequestDemoApiResponse): RequestDemoStatus {
    if (item.status) {
      return item.status;
    }

    if (item.resolved === 2 || item.resolved === true || item.resolved === 'yes') {
      return 'done';
    }

    return 'new';
  }

  private resolveAction(status: RequestDemoStatus): { label: string; nextStatus: RequestDemoStatus } | null {
    if (status === 'new') {
      return { label: this.processButtonLabel, nextStatus: 'in_progress' };
    }

    if (status === 'in_progress') {
      return { label: this.doneButtonLabel, nextStatus: 'done' };
    }

    return null;
  }

  private processRequest(request: RequestDemoInterface, targetStatus: RequestDemoStatus) {
    const previousStatus = request.status;
    const previousUpdatedAt = request.updatedAt;
    const processedAt = new Date().toISOString();

    const payload: ProcessRequestDemoPayload = {
      request: { ...request },
      transition: {
        from: request.status,
        to: targetStatus
      },
      processedAt
    };

    this.demoService.processRequestDemo(payload)
      .subscribe({
        next: (response) => {
          const nextStatus = response?.status ?? response?.transition?.to ?? targetStatus;
          const nextUpdatedAt = response?.updatedAt ?? response?.processedAt ?? processedAt;
          this.setStatus(request, nextStatus, nextUpdatedAt);
          this.loadRequestDemoList();
        },
        error: (error) => {
          console.error('Failed to process demo request', error);
          this.setStatus(request, previousStatus, previousUpdatedAt);
        }
      });
  }

  private loadRequestDemoList() {
    this.demoService.getRequestDemoList()
      .subscribe((data: RequestDemoApiResponse[]) => {
        this.tableData = (data || []).map(item => this.mapToRequest(item));
        this.demoService.countDemoRequested$.next(this.tableData.length);
      });
  }

  private setStatus(request: RequestDemoInterface, status: RequestDemoStatus, updatedAt?: string) {
    request.status = status;
    request.updatedAt = updatedAt ?? new Date().toISOString();
    if (this.table) {
      this.table.updateData([{
        id: request.id,
        status: request.status,
        updatedAt: request.updatedAt
      }]);
    }
  }

  private openDetailsDialog(request: RequestDemoInterface) {
    const data: RequestDemoDetailDialogData = {
      request,
      statusLabel: this.statusLabels[request.status] ?? request.status
    };

    this.dialog.open(RequestDemoDetailDialogComponent, {
      width: '800px',
      data
    });
  }
}
